import { extend, override } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import DiscussionHero from 'flarum/forum/components/DiscussionHero';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import CommentPost from 'flarum/forum/components/CommentPost';
import PageStructure from 'flarum/forum/components/PageStructure';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import listItems from 'flarum/common/helpers/listItems';
import humanTime from 'flarum/common/helpers/humanTime';
import Avatar from 'flarum/common/components/Avatar';
import username from 'flarum/common/helpers/username';
import Link from 'flarum/common/components/Link';

/**
 * Desktop post pages in the home page's frame (Reddit / X style): the same header, left menu and right
 * sidebar stay in place, and only the middle column changes to the post.
 *
 *  - Left: Flarum's own menu (IndexSidebar: Start a Discussion, All Discussions, Messages...) with the
 *    theme's grouped tag menu. The grouped menu is one element moved between pages (parked while another
 *    page shows), so its open groups and scroll position carry over instead of being rebuilt.
 *  - Right: the same sidebar element, showing the post's main tag (about card, ad, Most Read in that tag)
 *    and the "Follow us" links. Back on the home page it switches back to the home version.
 *  - Middle: a back arrow with the tags and title (instead of the full-width hero), the posts, and an action
 *    row under the opening post (Reply, Follow, Share, Save) instead of the column that held them.
 *
 * Desktop only (1100px and up, like the home grid). Phones and tablets keep Flarum's post page.
 */

let helpers = null; // from index.js: isDesktop, buildGroupedNav, injectRightSidebar, followLinks, safe
let page = null; // the DiscussionPage being shown
let wasOn = null;
const parked = {
  nav: null,
  navScroll: 0,
  right: null
};
export function postLayoutOn() {
  return !!(helpers && helpers.isDesktop());
}

/** The DiscussionPage on screen (null elsewhere) */
export function postPage() {
  return page;
}

/** The tag the right sidebar follows on a post: its most specific main tag ('' if none) */
export function postTagSlug() {
  const d = page && page.discussion;
  if (!d || typeof d.tags !== 'function') return '';
  const tags = (d.tags() || []).filter(Boolean);
  if (!tags.length) return '';
  const isPrimary = t => t.position && t.position() !== null && t.position() !== undefined;
  const parentOf = t => typeof t.parent === 'function' ? t.parent() : null;
  const primaries = tags.filter(t => isPrimary(t) || parentOf(t) && isPrimary(parentOf(t)));
  const pool = primaries.length ? primaries : tags;
  // a sub-tag (Toronto under Canada) says more than its parent
  const child = pool.find(t => parentOf(t) && pool.indexOf(parentOf(t)) !== -1) || pool.find(t => parentOf(t));
  const pick = child || pool.sort((a, b) => (a.position?.() ?? 999) - (b.position?.() ?? 999))[0];
  return pick && pick.slug ? pick.slug() : '';
}

/** Leaving a page with the frame: keep the menu and sidebar elements for the next page */
export function parkLayout() {
  const gn = document.querySelector('.Voucha-groupedNav');
  if (gn) {
    const scroller = gn.closest('.sideNav');
    parked.navScroll = scroller ? scroller.scrollTop : 0;
    parked.nav = gn;
    gn.remove();
  }
  const rs = document.querySelector('.Voucha-rightSidebar');
  if (rs) {
    parked.right = rs;
    rs.remove();
  }
}

/** Forget a parked menu (the tag list changed, so it gets rebuilt) */
export function dropParkedNav() {
  parked.nav = null;
}

/** The parked grouped menu, put into this page's menu (true if there was one) */
export function attachParkedNav(nav) {
  if (!parked.nav || !nav) return false;
  nav.classList.add('Voucha-navReplaced');
  nav.appendChild(parked.nav);
  parked.nav = null;
  const scroll = parked.navScroll;
  requestAnimationFrame(() => {
    const scroller = nav.matches('.sideNav') ? nav : nav.closest('.sideNav');
    if (scroller) scroller.scrollTop = scroll;
  });
  return true;
}

/** The parked right sidebar, put into this page's grid (null if there was none) */
export function takeParkedRight(container) {
  if (!parked.right || !container) return null;
  const rs = parked.right;
  parked.right = null;
  container.appendChild(rs);
  return rs;
}
function findVnode(node, test, depth) {
  if (depth === void 0) {
    depth = 0;
  }
  if (!node || depth > 4) return null;
  if (test(node)) return node;
  const kids = Array.isArray(node.children) ? node.children : [];
  for (const k of kids) {
    const hit = k && typeof k === 'object' ? findVnode(k, test, depth + 1) : null;
    if (hit) return hit;
  }
  return null;
}
function back(e) {
  e.preventDefault();
  if (app.history && app.history.back) app.history.back();else m.route.set(app.route('index'));
}

/** Bot posts start with the title as a heading; the header above already shows it */
function hideRepeatedTitle(root) {
  const d = page && page.discussion;
  if (!d) return;
  const body = root.querySelector('.PostStream-item[data-index="0"] .Post-body');
  if (!body) return;
  // the heading comes after the picture in bot posts: look at the first few blocks
  const norm = s => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const want = norm(d.title());
  const blocks = Array.prototype.slice.call(body.children, 0, 4);
  blocks.forEach(el => {
    if (/^H[1-3]$/.test(el.tagName)) el.classList.toggle('Voucha-repeatedTitle', norm(el.textContent) === want);
  });
}
function apply() {
  const root = document.querySelector('.DiscussionPage');
  if (!root || !postLayoutOn()) {
    // narrowed below desktop on a post: the sidebar leaves with the layout
    if (root) {
      const rs = root.querySelector('.Voucha-rightSidebar');
      if (rs) helpers.removeRightSidebar();
    }
    document.body.classList.remove('voucha-post');
    if (!document.querySelector('.IndexPage')) document.body.classList.remove('voucha-index');
    return;
  }
  const container = root.querySelector('.Page-container');
  if (!container) return; // still loading: the frame comes with the post
  // voucha-index: the home page's menu and sidebar styles; voucha-post: the post page's own layout
  document.body.classList.add('voucha-index', 'voucha-post');
  helpers.buildGroupedNav();
  if (page && page.discussion) {
    helpers.injectRightSidebar();
    hideRepeatedTitle(root);
  } else if (!container.querySelector('.Voucha-rightSidebar')) {
    // still loading: keep the last page's sidebar showing as it was; it switches to the post's tag on arrival
    takeParkedRight(container);
  }
}
export default function vouchaPostLayout(h) {
  helpers = h;
  extend(DiscussionPage.prototype, 'oninit', function () {
    page = this;
  });
  extend(DiscussionPage.prototype, 'oncreate', function () {
    helpers.safe(apply);
  });
  extend(DiscussionPage.prototype, 'onupdate', function () {
    helpers.safe(apply);
  });
  extend(DiscussionPage.prototype, 'onremove', function () {
    if (postLayoutOn()) parkLayout();
    if (page === this) page = null;
    document.body.classList.remove('voucha-post');
    if (!document.querySelector('.IndexPage')) document.body.classList.remove('voucha-index');
  });

  // While a post loads, Flarum shows only a spinner in place of the whole page, so the menu, sidebar and
  // background vanished for a moment (it looked like a reload). Here the frame stays and only the middle waits.
  override(DiscussionPage.prototype, 'view', function (original) {
    if (!postLayoutOn() || this.loadError || !this.loading && this.discussion || !PageStructure) return original();
    return m(PageStructure, {
      className: "DiscussionPage",
      sidebar: () => m(IndexSidebar, null)
    }, m("div", {
      className: "DiscussionPage-stream Voucha-postLoading"
    }, m(LoadingIndicator, {
      display: "block"
    })));
  });

  // Left column: the home page's menu instead of the post's action column
  override(DiscussionPage.prototype, 'sidebar', function (original) {
    if (!postLayoutOn() || !IndexSidebar) return original();
    return m(IndexSidebar, null);
  });

  // Middle column, top: back arrow, tags and title (the hero itself is hidden in this layout)
  extend(DiscussionPage.prototype, 'view', function (vdom) {
    if (!postLayoutOn() || !this.discussion || !vdom) return;
    const kids = Array.isArray(vdom.children) ? vdom.children : [];
    const stream = kids.find(k => k && k.attrs && k.attrs.className === 'DiscussionPage-stream');
    if (!stream || !Array.isArray(stream.children)) return;
    const d = this.discussion;
    const created = d.createdAt && d.createdAt();
    const author = d.user && d.user();
    const ai = author && author.attribute && author.attribute('vouchaAiAuthor');
    // Reddit-style: [back] (avatar) name · tags · date · AI label, then the title. The opening post's own
    // byline and avatar column are hidden in this layout, so its text uses the full width.
    stream.children.unshift(m("div", {
      className: "Voucha-postHead"
    }, m("div", {
      className: "Voucha-postHead-meta"
    }, m("a", {
      className: "Voucha-postBack",
      href: app.route('index'),
      onclick: back,
      "aria-label": "Back",
      title: "Back"
    }, m("i", {
      className: "fas fa-arrow-left",
      "aria-hidden": "true"
    })), author ? m(Link, {
      className: "Voucha-postHead-user",
      href: app.route.user(author)
    }, m(Avatar, {
      user: author,
      className: "Voucha-postHead-avatar"
    }), m("span", {
      className: "Voucha-postHead-name"
    }, username(author))) : m("span", {
      className: "Voucha-postHead-name"
    }, "[deleted]"), m("span", {
      className: "Voucha-postHead-dot"
    }, "\xB7"), m("div", {
      className: "Voucha-postHead-tags"
    }, m(DiscussionHero, {
      discussion: d
    })), created ? m("span", {
      className: "Voucha-postHead-age"
    }, "\xB7 ", humanTime(created)) : null, ai ? m("span", {
      className: "VouchaAiLabel",
      title: "Written with AI from the news reports linked in the post"
    }, m("i", {
      className: "fas fa-robot",
      "aria-hidden": "true"
    }), " AI-generated") : null), m("h1", {
      className: "Voucha-postHead-title"
    }, d.title())));
  });

  // Under the opening post: Reply, Follow, Share, Save (the items the post's column used to hold)
  extend(CommentPost.prototype, 'view', function (vdom) {
    const post = this.attrs.post;
    if (!postLayoutOn() || !page || !page.discussion || !post || post.number() !== 1) return;
    if (post.discussion() !== page.discussion || !vdom || !Array.isArray(vdom.children)) return;
    const items = page.sidebarItems();
    ['scrubber', 'vouchaFollowLinks'].forEach(k => items.has(k) && items.remove(k));
    // at the end of the post's main column, so it lines up with the text (not under the avatar)
    const main = findVnode(vdom, v => v.attrs && v.attrs.className === 'Post-main');
    const target = main && Array.isArray(main.children) ? main.children : vdom.children;
    target.push(m("ul", {
      className: "Voucha-postActions"
    }, listItems(items.toArray())));
  });

  // Crossing 1100px swaps the whole layout: redraw so the left column and header follow
  window.addEventListener('resize', () => {
    const on = postLayoutOn();
    if (wasOn !== null && on !== wasOn && page) m.redraw();
    wasOn = on;
  });
  wasOn = postLayoutOn();
}

/** The right sidebar's "Follow us" slot: the Google / WhatsApp links, on post pages only */
export const FollowSlot = {
  view() {
    if (!postLayoutOn() || !page || !page.discussion || !helpers) return null;
    const links = helpers.followLinks();
    return links ? m("div", {
      className: "Voucha-rightSidebar-section Voucha-followSide"
    }, links) : null;
  }
};